/**
 * @fileoverview The ONE locale-keyed `Intl` formatter cache
 *               (ES-I18N-FOLLOW-ON F2a, 2026-08-01 · dual-use Lane A2, 2026-08-02).
 *
 * Constructing an `Intl.*Format` is expensive, so the pre-seam idiom was a
 * module-level singleton:
 *
 *     const DATE_FMT = new Intl.DateTimeFormat('en-US');   // ← constructed once…
 *                                                          //   …and English forever
 *
 * ECO-RESIDUALS-CLEANUP-001 R3 replaced that in planner-web with a locale-keyed
 * `Map`, which is correct — and was then hand-rolled **13 times**, the same
 * eight lines under thirteen different constant names
 * (`CURRENCY_FORMATTERS`, `SPREAD_GRID_INT_FORMATTERS`,
 * `PRINTABLE_DATE_FORMATTERS`, …). The remaining conversion surface was 341
 * sites, so replicating the idiom would have made it ~230 copies of one
 * function. It lives here instead, beside `intlLocaleFor` / `useIntlLocale`,
 * because a formatter cache keyed by locale is a property of the LANGUAGE
 * SYSTEM.
 *
 * Canonical use:
 *
 *     import { useIntlLocale, getNumberFormatter } from '@puffin-core/i18n-bootstrap';
 *
 *     function Row({ amount }) {
 *       const locale = useIntlLocale();                       // 'en-US' | 'es-US'
 *       const fmt = getNumberFormatter(locale, { style: 'currency', currency: 'USD' });
 *       return <td>{fmt.format(amount)}</td>;
 *     }
 *
 * In a PURE helper, take `locale` as a REQUIRED parameter and thread it from the
 * component boundary — never reach for a default.
 *
 * ## THIS FILE IS THE IMPLEMENTATION; the ESM twin is a shim
 *
 * Lane A2 (2026-08-02) moved the body here, to CJS, and reduced
 * `lib/esm/formatterCache.js` to a zero-logic re-export. The reason is
 * `@puffin-core/money`: `formatUsd` is the ecosystem's single USD formatter and
 * the API tier (100% CommonJS — QuickBooks report builders, CLI scripts,
 * outbound customer email) must be able to `require()` it. `formatUsd` is one
 * line over `getNumberFormatter`, so the cache had to cross the boundary first.
 *
 * Only this leaf is dual. The package BARREL stays ESM-only on purpose — it
 * pulls `createAppI18n` (i18next) and the React hooks, none of which any CJS
 * caller wants, and converting them would drag `react-i18next` into the API
 * tier. This module imports **nothing**, which is what makes it safe to expose
 * under both conditions.
 *
 * Web and API never share a process, so "one cache instance" is not a
 * correctness requirement — **one implementation** is, and that is what this
 * arrangement guarantees.
 *
 * ## `locale` is required, deliberately
 *
 * These functions THROW on a missing locale rather than defaulting to `'en-US'`.
 * A silent default is the exact shape this initiative exists to delete —
 * temporal-core's hidden `locale = 'en-US'` survived a sweep dedicated to
 * finding it — and a cache that reintroduced one would quietly re-create the
 * bug at every call site that forgot the argument.
 *
 * ## Cache key
 *
 * `locale` + the options object serialized with its keys SORTED, so
 * `{dateStyle,timeStyle}` and `{timeStyle,dateStyle}` describe one formatter and
 * not two. Keys whose value is `undefined` are dropped, matching `Intl`'s own
 * treatment of them as absent. The cache is unbounded by design: the key space
 * is (supported locales × the option shapes literally written in the source),
 * which is small, fixed at build time, and lives for the page's lifetime —
 * exactly what the 13 hand-rolled Maps already assumed.
 *
 * ## Reachable as a SUBPATH, deliberately
 *
 * Also exported as `@puffin-core/i18n-bootstrap/formatter-cache`. This module
 * imports NOTHING — no react, no i18next, no barrel — so `@puffin-core/page-shell`
 * can consume it without importing the i18n-bootstrap BARREL. That distinction is
 * load-bearing: I18N-BOOTSTRAP-DEPCYCLE-001 records a real module-eval crash from
 * the cycle `page-shell barrel → <UserProfilePage> → i18n-bootstrap barrel →
 * createAppI18n → page-shell barrel (partially initialized → undefined)`. A
 * barrel-to-barrel edge risks re-opening it; a leaf module cannot.
 *
 * @module @puffin-core/i18n-bootstrap/formatterCache
 */

'use strict';

/** One cache per constructor, so a NumberFormat can never satisfy a DateTimeFormat lookup. */
const CACHES = new Map();

/**
 * Build the cache key for a locale + options pair.
 *
 * Sorting the option keys makes the key canonical: two spellings of the same
 * formatter collapse to one entry.
 *
 * @param {string} locale - resolved Intl locale
 * @param {object} options - Intl constructor options
 * @returns {string} stable cache key
 */
function cacheKey(locale, options) {
  const entries = Object.entries(options)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : 1));
  // One JSON document, not a delimited string: nesting the two parts in an
  // array makes the boundary structural, so no separator character has to be
  // chosen at all and none can collide with a locale tag or an option value.
  return JSON.stringify([locale, entries]);
}

/**
 * Resolve (and memoize) a formatter of the given kind.
 *
 * @param {string}   kind - registry key, also the cache partition
 * @param {Function} Ctor - the `Intl` constructor
 * @param {string}   locale - resolved Intl locale from `useIntlLocale()`
 * @param {object}   options - constructor options
 * @returns {object} the cached formatter instance
 */
function resolveFormatter(kind, Ctor, locale, options) {
  if (typeof locale !== 'string' || locale.length === 0) {
    throw new TypeError(
      `${kind}: locale is required (non-empty string). Resolve it with \`useIntlLocale()\` ` +
        'from `@puffin-core/i18n-bootstrap` at the component boundary, or take `locale` as a ' +
        'REQUIRED parameter in a pure helper. There is deliberately no default: a silent ' +
        "'en-US' is the bug this seam exists to remove.",
    );
  }
  let cache = CACHES.get(kind);
  if (!cache) {
    cache = new Map();
    CACHES.set(kind, cache);
  }
  const key = cacheKey(locale, options);
  let formatter = cache.get(key);
  if (!formatter) {
    formatter = new Ctor(locale, options);
    cache.set(key, formatter);
  }
  return formatter;
}

/**
 * Locale-aware `Intl.NumberFormat`, memoized per locale + options.
 *
 * @param {string} locale - from `useIntlLocale()` (REQUIRED)
 * @param {object} [options] - `Intl.NumberFormat` options
 * @returns {Intl.NumberFormat}
 */
function getNumberFormatter(locale, options = {}) {
  return resolveFormatter('getNumberFormatter', Intl.NumberFormat, locale, options);
}

/**
 * Locale-aware `Intl.DateTimeFormat`, memoized per locale + options.
 *
 * @param {string} locale - from `useIntlLocale()` (REQUIRED)
 * @param {object} [options] - `Intl.DateTimeFormat` options
 * @returns {Intl.DateTimeFormat}
 */
function getDateTimeFormatter(locale, options = {}) {
  return resolveFormatter('getDateTimeFormatter', Intl.DateTimeFormat, locale, options);
}

/**
 * Locale-aware `Intl.RelativeTimeFormat`, memoized per locale + options.
 *
 * @param {string} locale - from `useIntlLocale()` (REQUIRED)
 * @param {object} [options] - `Intl.RelativeTimeFormat` options
 * @returns {Intl.RelativeTimeFormat}
 */
function getRelativeTimeFormatter(locale, options = {}) {
  return resolveFormatter('getRelativeTimeFormatter', Intl.RelativeTimeFormat, locale, options);
}

/**
 * Locale-aware `Intl.ListFormat`, memoized per locale + options.
 *
 * @param {string} locale - from `useIntlLocale()` (REQUIRED)
 * @param {object} [options] - `Intl.ListFormat` options
 * @returns {Intl.ListFormat}
 */
function getListFormatter(locale, options = {}) {
  return resolveFormatter('getListFormatter', Intl.ListFormat, locale, options);
}

/**
 * Locale-aware `Intl.PluralRules`, memoized per locale + options — the plural selector
 * (`./pluralKeys`) resolves one per translation call, so construction is paid once per locale.
 *
 * @param {string} locale - resolved Intl locale (REQUIRED)
 * @param {object} [options] - `Intl.PluralRules` options (`type: 'ordinal'`, …)
 * @returns {Intl.PluralRules}
 */
function getPluralRules(locale, options = {}) {
  return resolveFormatter('getPluralRules', Intl.PluralRules, locale, options);
}

/**
 * Drop every cached formatter.
 *
 * Test-only: identity assertions ("the same locale returns the SAME instance")
 * are only meaningful against a known-empty cache, and a module-level Map
 * outlives `clearMocks`. Never call this from application code — it would throw
 * away the construct-once win the cache exists for.
 *
 * @returns {void}
 */
function __resetFormatterCacheForTests() {
  CACHES.clear();
}

/**
 * Shift a finite number's SHORTEST decimal representation by `places` powers of ten, without a
 * binary multiplication (which is where `85.05 * 10` and `1.005 * 100` lose their halves).
 *
 * @param {number} value
 * @param {number} places
 * @returns {number}
 */
function shiftDecimal(value, places) {
  const [mantissa, exponent = '0'] = String(value).split('e');
  return Number(`${mantissa}e${Number(exponent) + places}`);
}

/**
 * Round a number to `fractionDigits` exactly as the number formatter will SHOW it
 * (MUTATION-BURNDOWN-001, coordinator ruling 2026-10-19: "the rule is 'agree with what the formatter
 * shows', so it belongs beside the formatter").
 *
 * The contract is THIS runtime's `Intl.NumberFormat` (`getNumberFormatter`): ROUND_HALF_EXPAND
 * (halves away from zero) applied to the value's SHORTEST decimal representation — `85.05` is
 * rounded as the decimal 85.05, not as the binary double 85.0499999…, so it reads 85.1 like the
 * formatter does. Use it when a decision must agree with a displayed figure (a tier graded beside a
 * percent): grading on the raw value lets "100%" sit beside "Near". The two traps it replaces,
 * measured against the formatter: `Number(v.toFixed(p))` rounds the binary value (85.05 @ 1 →
 * 85.0, the formatter shows 85.1), and `Math.round(v * 10 ** p) / 10 ** p` multiplies in binary
 * (1.005 @ 2 → 1.00, the formatter shows 1.01). A parity test against `getNumberFormatter` keeps
 * this honest: a runtime whose formatter rounds differently turns it red.
 *
 * @param {number} value - a finite number
 * @param {number} fractionDigits - whole number of decimal places, 0..20 (Intl's own range)
 * @returns {number} the value the formatter would display, as a number
 * @throws {TypeError} when `value` is not a finite number, or `fractionDigits` is not 0..20
 */
function roundAsDisplayed(value, fractionDigits) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    // `String` for a number: JSON would print NaN and Infinity as "null".
    const received = typeof value === 'number' ? String(value) : JSON.stringify(value);
    throw new TypeError(`roundAsDisplayed: value must be a finite number, received ${received}`);
  }
  if (!Number.isInteger(fractionDigits) || fractionDigits < 0 || fractionDigits > 20) {
    throw new TypeError(
      `roundAsDisplayed: fractionDigits must be a whole number from 0 to 20, received ${JSON.stringify(fractionDigits)}`,
    );
  }
  const shifted = shiftDecimal(value, fractionDigits);
  // Halves away from zero, symmetric for negatives (Math.round alone rounds -2.5 to -2).
  const rounded = Math.sign(shifted) * Math.round(Math.abs(shifted));
  return shiftDecimal(rounded, -fractionDigits);
}

module.exports = {
  roundAsDisplayed,
  getNumberFormatter,
  getDateTimeFormatter,
  getRelativeTimeFormatter,
  getListFormatter,
  getPluralRules,
  __resetFormatterCacheForTests,
};
